import { observer } from 'mobx-react-lite';
import { createContext, useCallback, useContext, useEffect } from 'react';

import { useAmbientTintEffect } from '@/features/ambient-theme';
import { electronApis } from '@/features/electron-api';
import { useShellThemeEffect } from '@/features/shell-theme';
import { uiStore, type Theme, type ThemePreference } from '@/stores/ui.store';
import { isWindows } from '@/utils';

type ThemeContextType = {
  theme: Theme;
  themePreference: ThemePreference;
  setThemePreference: (theme: ThemePreference) => void;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = observer(
  ({ children }: { children: React.ReactNode }) => {
    const theme = uiStore.theme;
    const themePreference = uiStore.themePreference;

    const setThemePreference = useCallback(
      (value: ThemePreference) => uiStore.setThemePreference(value),
      []
    );

    const toggleTheme = useCallback(() => {
      uiStore.setThemePreference(theme === 'dark' ? 'light' : 'dark');
    }, [theme]);

    useEffect(() => {
      document.body.classList.remove('light', 'dark');
      document.body.classList.add(theme);
    }, [theme]);

    // Keep the Windows titleBarOverlay in sync with the active theme so the
    // native min/max/close buttons blend with the app chrome instead of
    // showing as a dark patch in light mode.
    useEffect(() => {
      if (!isWindows()) return;
      electronApis.window.setTitleBarOverlayTheme(theme);
    }, [theme]);

    useAmbientTintEffect(theme);
    useShellThemeEffect();

    return (
      <ThemeContext.Provider
        value={{ theme, themePreference, setThemePreference, toggleTheme }}
      >
        {children}
      </ThemeContext.Provider>
    );
  }
);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
